import React, {useEffect, useState} from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ArrWidgetWrapper from '../../../common/arr-widget-wrapper/ArrWidgetWrapper';
import './TaggingWidget.scss';
import {t} from 'i18next';
import {ReviewTagCount, TaggingWidgetData} from '../../dashboardTypes';
import {useTaggingWidget} from '../../../../hooks/dashboard/useDashboard';
import {CustomizeTagWidget} from '../customise-tag-widget/CustomizeTagWidget';

interface TaggingData {
  tag: string;
  responsive: number;
}

export interface TaggingWidgetProps {
  title?: string;
  tableTitle?: string;
  showInfoIcon?: boolean;
  showSettingsIcon?: boolean;
  showExpandIcon?: boolean;
  onReplaceClick?: () => void;
  reviewTagCount?: ReviewTagCount;
  tagDefault?: boolean;
}

const columns = [
  {key: 'tag', label: 'dashboard.taggingWidget.tag'},
  {key: 'count', label: 'dashboard.taggingWidget.count'},
];

const TaggingWidget: React.FC<TaggingWidgetProps> = ({
  title = t('dashboard.taggingWidget.title'),
  tableTitle = 'Selected Tags',
  showInfoIcon = false,
  showSettingsIcon = false,
  showExpandIcon = false,
  onReplaceClick,
  reviewTagCount = {} as ReviewTagCount,
  tagDefault = true,
}) => {
  const [data, setData] = useState<TaggingWidgetData>();

  const {
    data: taggingWidData,
    isLoading: loading,
    isError: error,
  } = useTaggingWidget({...reviewTagCount, isDefaultWidget: tagDefault});

  useEffect(() => {
    setData(taggingWidData);
  }, [taggingWidData]);

  const onTagConfigUpdate = (newData: TaggingWidgetData) => {
    setData(newData);
  };

  const privilegedCount =
    data?.headerData.find((header) => header.tagClassificationId === -2)
      ?.docCount ?? 0;
  const responsiveCount =
    data?.headerData.find((header) => header.tagClassificationId === -1)
      ?.docCount ?? 0;

  const taggingData =
    data?.data?.map((item) => ({
      tag: item.tagName,
      count: item.docCount,
    })) ?? [];
  const [isCustomiseOpen, setIsCustomiseOpen] = useState(false);
  const handleDrawer = (value: React.SetStateAction<boolean>) =>
    setIsCustomiseOpen(value);
  const dataTags = taggingData.length > 0 ? taggingData : [...Array(5)];
  return (
    <ArrWidgetWrapper
      title={title}
      showInfo={showInfoIcon}
      showSettings={showSettingsIcon}
      showReplace={showExpandIcon}
      onReplaceClick={onReplaceClick}
      onSettingsClick={() => handleDrawer(true)}
      loading={loading}
      error={error}
      tooltipContent={t('dashboard.taggingWidget.tooltip')}
    >
      <CustomizeTagWidget
        showDrawer={isCustomiseOpen}
        handleDrawer={handleDrawer}
        reviewTagCountData={data}
        onSuccess={onTagConfigUpdate}
      />
      <Box className="tagging-root" data-testid="tagging-widget">
        <Box className="summary-row">
          <Box className="summary-item">
            <Typography
              className="typo-text label2_size"
              data-testid={`tagging-widget-priviliege`}
            >
              {t('dashboard.taggingWidget.privileged')}
            </Typography>
            <Typography
              className={`typo-text display2_size ${privilegedCount === 0 ? 'zero-count' : ''}`}
              data-testid={`tagging-widget-priviliege-count`}
            >
              {privilegedCount}
            </Typography>
          </Box>
          <Box className="summary-item">
            <Typography
              className="typo-text label2_size"
              data-testid={`tagging-widget-responsive`}
            >
              {t('dashboard.taggingWidget.responsive')}
            </Typography>
            <Typography
              className={`typo-text display2_size ${responsiveCount === 0 ? 'zero-count' : ''}`}
              data-testid={`tagging-widget-responsive-count`}
            >
              {responsiveCount}
            </Typography>
          </Box>
        </Box>

        <Box className="tagging-table">
          <Typography
            className="typo-text subhead5_size"
            data-testid={`tagging-widget-table-title`}
          >
            {tableTitle}
          </Typography>
          <Table className="table-container">
            <TableHead>
              <TableRow>
                {columns.map((header) => (
                  <TableCell
                    key={header.label}
                    className="tagging-table-header"
                    sx={{width: 'tag' === header.key ? '50%' : '25%'}}
                    data-testid={`tagging-widget-table-column-${header.key}`}
                  >
                    {t(header.label)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {dataTags.map((r, idx) => (
                <TableRow key={idx.toLocaleString()}>
                  {columns.map((col) => {
                    const value = r ? r[col.key as keyof TaggingData] : '';
                    return (
                      <TableCell
                        key={col.key}
                        className={`table-row table-row-zero-state`}
                        data-testid={`tagging-widget-table-column-${col.key}-${idx}`}
                      >
                        {value}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Box>
    </ArrWidgetWrapper>
  );
};

export default TaggingWidget;
